// Resolve the in-app navigation target for a notification click.
//
// One shared resolver so the bell dropdown, the notification sidebar and the
// full /notifications page all agree on where a row leads.
//
// Order of precedence:
//   1. The server-persisted `redirect_path` (authoritative, per notification).
//      It works for every notification_type and is the preferred source for
//      types whose target is context-dependent (e.g. new_pitch_received points
//      at a different place for the club flow vs the agent-tender flow, so it
//      has no fallback). Since the B8 type split the agent-tender flow emits
//      agent_tender_pitch_*, so legacy pitch_response_* rows get a club-flow
//      fallback below. Only internal paths are honored.
//   2. A per-type fallback for the unambiguous agent-tender lifecycle events,
//      used ONLY when the backend did not persist a redirect_path (Matrix v0.3
//      section 6). These point at the poster's own agent-tender feed and never
//      name a club, so they cannot leak the anonymous counterpart.
//   3. The legacy meeting fallback (/meetings/<id>) for older meeting rows that
//      predate redirect_path.
//
// Returns an internal path string, or null when nothing safe resolves. Callers
// decide what null means: the bell falls back to /notifications, the list page
// stays put.

// A safe internal target is a non-empty string starting with a single "/"
// (rejects external "http(s)://" and protocol-relative "//host" links).
export const isInternalPath = (path) =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

// Unambiguous agent-tender types -> the poster's own agent-tender feed.
// Shared/ambiguous types are deliberately absent (see precedence rule 1).
const AGENT_TENDER_FEED = "/agent-tenders/mine";
const TYPE_FALLBACK_PATHS = {
  agent_tender_posted: AGENT_TENDER_FEED,
  agent_tender_verified: AGENT_TENDER_FEED,
  agent_tender_declined: AGENT_TENDER_FEED,
  agent_tender_superseded: AGENT_TENDER_FEED,
  agent_tender_expiring_soon: AGENT_TENDER_FEED,
  agent_tender_expired: AGENT_TENDER_FEED,
  agent_tender_revoked_fraud: AGENT_TENDER_FEED,
  verification_opened: AGENT_TENDER_FEED,
  // Agency-activity types (D-011): unambiguous poster-side targets.
  agency_task_created: "/agency/taskboard",
  player_assigned_to_agent: "/agent/mysquad",
  // Scout Requests (M4): single route valid for each type's whole audience —
  // club inbox for the received notification, the agent's my-recs for on-radar.
  // The server also sends redirect_path (precedence 1); these are the fallback.
  scout_recommendation_received: "/scouting/requests",
  scout_recommendation_on_radar: "/scout-requests/my-recommendations",
  // TP-Last-Minute (M4): the thin notifications. Server sends redirect_path
  // (precedence 1); these are the fallback if it is ever absent. The request
  // events emit as message_request_* (there is no last_minute_request_received
  // backend type).
  message_request_received: "/inbox?tab=requests",
  message_request_accepted: "/inbox",
  last_minute_expiry_warning: "/last-minute/my-listings",
  // Emitted by the M8 last-minute events wave (BE PR #148): the listing
  // owner's expired notice → their own listings; the reveal-granted event
  // persists /last-minute?listing=<id> server-side (context-dependent id), so
  // its fallback is the plain feed.
  last_minute_expired: "/last-minute/my-listings",
  last_minute_reveal_granted: "/last-minute",
  // TP-Friendlies (M4): a new application on your friendly request → your
  // requests. Server sends redirect_path (precedence 1); this is the fallback
  // if absent.
  friendly_new_application: "/friendlies/mine",
  // Friendlies lifecycle (V2 wave): chat opened with the applicant → the chat;
  // request closed (outcome-neutral) → the applicant's applications list.
  friendly_chat_started: "/inbox",
  friendly_request_closed: "/friendlies/applications",
  // Lawyers/Organizer directory events (V2 NoteRole): all land in the inbox
  // (the request section for lawyers, the opened channel otherwise).
  lawyer_contact_request_received: "/inbox",
  lawyer_contact_request_accepted: "/inbox",
  organizer_contacted: "/inbox",
  // Chat-timer lifecycle: rows persisted before the notify() migration carry
  // NO redirect_path (the legacy emitter sent it realtime-only), so this
  // fallback is what makes those older rows clickable after a reload.
  chat_timer_expiring_soon: "/inbox",
  chat_timer_expired: "/inbox",
  chat_timer_revived: "/inbox",
  // Mandate lifecycle (V2 NoteRole): player-facing events land on the profile
  // (mandate banner/section), agent-facing on My Squad.
  mandate_invite_received: "/profile",
  mandate_extended: "/profile",
  mandate_accepted: "/agent/mysquad",
  mandate_declined: "/agent/mysquad",
  mandate_withdrawn: "/agent/mysquad",
  // Pitch-response generations (B8 type split): the agent-tender flow emits
  // agent_tender_pitch_* (targets mirror the backend AGENT_TENDER_DEEPLINKS),
  // which leaves legacy pitch_response_* rows as club-flow pitch outcomes for
  // the sending agent — /agent/tenders is that flow's role_route default.
  // redirect_path (precedence 1) still wins wherever it was persisted.
  agent_tender_pitch_received: "/agent-tenders/mine?tab=responses",
  agent_tender_pitch_accepted: "/inbox",
  agent_tender_pitch_rejected: "/agent-tenders",
  pitch_response_accepted: "/agent/tenders",
  pitch_response_rejected: "/agent/tenders",
  // pitch_expired / pitch_expired_sender are deliberately ABSENT: the backend
  // role-routes them (agent vs coach vs club surfaces), so like
  // new_pitch_received they rely on the persisted redirect_path (precedence 1).
  // Saved-tender expiry warning for viewers who saved someone else's agent
  // tender → the public agent-tender feed (not /mine).
  saved_agent_tender_expiry: "/agent-tenders",
  // Agency lifecycle (V2 wave): partnership invites/accepts and admin
  // promotion all land on the agency page.
  agency_partnership_invited: "/agency",
  agency_partnership_accepted: "/agency",
  // The third state of the same invite sequence (mail 59), same wall.
  agency_partnership_declined: "/agency",
  agency_admin_promoted: "/agency",
  // club_admin_promoted and club_role_updated are deliberately ABSENT: both
  // events were removed on Marin's decision of 2026-08-03 — role changes
  // notify nothing (no mail, no bell, no event). Do not re-add fallbacks.
  // meeting_starting_soon (mail 58, BE #256) also has deliberately NO entry:
  // like the whole meeting_ family its target is /meetings/<id>, which is
  // row-dependent. Every row persists that redirect_path at birth
  // (dashboard/meeting_tasks.py), and realtime payloads additionally carry
  // meeting_id, which the legacy meeting_ fallback below resolves.
  // Chat handover (messages family): roster adds and contact-person handover
  // → the inbox.
  chat_participant_added: "/inbox",
  chat_contact_person_assigned: "/inbox",
  // ── Phase-4 wave (BE PRs #265–#270) ───────────────────────────────────────
  // Agency join-request/membership lane (notifications/agency_events.py):
  // members and admins land on the agency page; the DECLINED requester and the
  // REMOVED agent have no agency page any more, so the backend deliberately
  // routes them Home ('/') — mirrored here.
  agency_join_request_received: "/agency",
  agency_join_request_accepted: "/agency",
  agency_join_request_declined: "/",
  agency_member_left: "/agency",
  agency_membership_removed: "/",
  agency_partnership_revoked: "/agency",
  agency_task_status_changed: "/agency/taskboard",
  // K1: a request the responder engaged with ended → their responses view on
  // the marketplace page (mirrors the backend AGENT_TENDER_DEEPLINKS entry).
  agent_tender_request_ended: "/agent-tenders",
  // B-2 (Marin's verdict 2026-08-04): the responder-side ends of a response on
  // an agent request — the 24h warning and the expired notice. One audience
  // (the responder), one target: their responses on the marketplace page
  // (mirrors the backend AGENT_TENDER_DEEPLINKS entries).
  agent_tender_response_expiring: "/agent-tenders",
  agent_tender_response_expired: "/agent-tenders",
  // Messages family: the missed call and the contract-lane events all live in
  // the inbox (chat_call_notifications.py / contract_events.py).
  call_missed: "/inbox",
  contract_signer_declined: "/inbox",
  contract_signing_expired: "/inbox",
  // J14: the verified lawyer's own profile (role-neutral, like the mandate
  // profile targets above).
  lawyer_profile_verified: "/profile",
  // J11: the pending-requests banner renders on the agent Home.
  representation_request_received: "/",
  // Deliberately ABSENT (role-routed by the backend, redirect_path only, same
  // rule as pitch_expired above): club_member_left + club_request_posted
  // (role_route per club role), pitch_moved_to_deal + pitch_external_deal +
  // pitch_fully_signed (role_route 'pitch_outcome'), pinned_tender_promoted
  // (pinned_tender_redirect_for) and scheduled_request_schedule_removed
  // (role_route 'club_tenders'). fifa_licence_verified/_failed never create
  // bell rows at all (in_app=False, email-only) — nothing to route.
};

export const resolveNotificationPath = (notification) => {
  if (!notification) return null;
  const { redirect_path, notification_type, meeting_id } = notification;

  if (isInternalPath(redirect_path)) return redirect_path;

  if (notification_type && TYPE_FALLBACK_PATHS[notification_type]) {
    return TYPE_FALLBACK_PATHS[notification_type];
  }

  if (notification_type?.startsWith("meeting_") && meeting_id) {
    return `/meetings/${meeting_id}`;
  }

  return null;
};
